import geojsonValidation from 'geojson-validation'
import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { Workflow } from '../workflow'

class DrawCustomGeographies extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.drawcustomgeographies',
        group: 'Parsers',
        title: 'Draw Custom Features',
        description: `
        This component allows drawing new custom geospatial features.
      `,
        starred: true
      },
      [
        {
          name: 'geojson',
          type: WorkflowComponentParamType.GeoJsonDraw,
          title: 'Features',
          allowExpressions: false
        }
      ]
    )
    this.canBeCompacted = true
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (params.geojson && !isExpressionValue(params.geojson as WorkflowComponentValue)) {
      try {
        const geojson = JSON.parse(resolvedParamValues.geojson as string)
        if (!geojsonValidation.valid(geojson)) {
          problems.push({
            parameter: 'geojson',
            type: WorkflowParameterValueErrorType.WrongValue,
            message: 'Invalid GeoJSON string'
          })
        }
      } catch (e) {
        problems.push({
          parameter: 'geojson',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Invalid GeoJSON string'
        })
      }
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const features = this.getFeaturesFromGeojson(params.geojson as string)
    const sqlFeatures: Array<string> = []
    for (const feature of features) {
      const columns = {}
      const properties = feature.properties as Record<string, unknown>
      for (const prop in properties) {
        const value = properties[prop]
        if (typeof value === 'string') {
          if (connection.type === Provider.BigQuery || connection.type === Provider.DatabricksRest) {
            columns[prop] = `'${value.replace(/'/g, "\\'")}'`
          } else if (connection.type === Provider.Snowflake) {
            columns[prop] = `TO_CHAR('${value.replace(/'/g, "''")}')`
          } else {
            columns[prop] = `'${value.replace(/'/g, "''")}'`
          }
        } else {
          columns[prop] = value
        }
      }
      if (feature.geometry) {
        if (connection.type === Provider.BigQuery) {
          columns['geom'] = dedent`ST_GEOGFROM(
                                  '${JSON.stringify(feature.geometry)}'
                                )`
        } else if (connection.type === Provider.Snowflake) {
          columns['geom'] = dedent`TO_GEOGRAPHY(
                                  '${JSON.stringify(feature.geometry)}'
                                )`
        } else {
          columns['geom'] = dedent`${connection.processOutputGeogColumn(`ST_GEOMFROMGEOJSON(
  '${JSON.stringify(feature.geometry)}'
)`)}`
        }
      }
      sqlFeatures.push(
        Object.keys(columns)
          .map((x) => `${columns[x]} AS ${x}`)
          .join(',\n')
      )
    }
    if (codeType === WorkflowCodeType.Full) {
      codeType = WorkflowCodeType.FullNoCache
    } // to avoid adding features to the same table again when re-running the workflow
    let query = ''
    if (connection.type === Provider.DatabricksRest) {
      query = connection.getCreateTempTableQuery(
        outputTablename,
        dedent`SELECT /*+ REPARTITION(40, geom) */
                  ${sqlFeatures[0]}
                `,
        codeType,
        null,
        false
      )
    } else {
      query = connection.getCreateTempTableQuery(
        outputTablename,
        dedent`SELECT
                  ${sqlFeatures[0]}
                `,
        codeType,
        null,
        false
      )
    }

    if (!dryRun) {
      query += '\n'
      if (connection.type === Provider.DatabricksRest) {
        query += dedent(
          sqlFeatures
            .slice(1)
            .map(
              (x) => dedent`
                        INSERT INTO ${connection.quoteName(outputTablename)}
                        SELECT /*+ REPARTITION(40, geom) */
                          ${x};
                      `
            )
            .join('\n')
        )
        query = dedent`
          ${query}
          ${connection.optimizationStatsQuery(outputTablename)}
        `
      } else {
        query += dedent(
          sqlFeatures
            .slice(1)
            .map(
              (x) => dedent`
                        INSERT INTO ${connection.quoteName(outputTablename)}
                        SELECT
                          ${x};
                      `
            )
            .join('\n')
        )
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canBeConvertedToCTE(workflow: Workflow, componentName: string): boolean {
    const featuresFromGeojson = this.getFeaturesFromGeojson(
      (workflow.componentValues[componentName].geojson as WorkflowComponentValue).value as string
    )
    return featuresFromGeojson.length === 1 && super.canBeConvertedToCTE(workflow, componentName)
  }

  getFeaturesFromGeojson(geojson: string): Array<Record<string, unknown>> {
    const parsedGeojson = JSON.parse(geojson as string)
    let features: Array<Record<string, unknown>> = []
    if (parsedGeojson?.type === 'Feature') {
      features = [parsedGeojson]
    } else if (parsedGeojson?.type === 'FeatureCollection') {
      features = parsedGeojson.features
    } else {
      throw new Error('Invalid GeoJSON')
    }
    return features
  }

  async getCTECode({ params, connection }: WorkflowComponentCodeParams): Promise<string | null> {
    const features = this.getFeaturesFromGeojson(params.geojson as string)

    if (features.length !== 1) {
      return null
    }

    const feature = features[0]
    const columns = {}
    const properties = feature.properties as Record<string, unknown>
    for (const prop in properties) {
      const value = properties[prop]
      if (typeof value === 'string') {
        if (connection.type === Provider.BigQuery || connection.type === Provider.DatabricksRest) {
          columns[prop] = `'${value.replace(/'/g, "\\'")}'`
        } else if (connection.type === Provider.Snowflake) {
          columns[prop] = `TO_CHAR('${value.replace(/'/g, "''")}')`
        } else {
          columns[prop] = `'${value.replace(/'/g, "''")}'`
        }
      } else {
        columns[prop] = value
      }
    }
    if (feature.geometry) {
      if (connection.type === Provider.BigQuery) {
        columns['geom'] = dedent`ST_GEOGFROMGEOJSON(
                                '${JSON.stringify(feature.geometry)}'
                              )`
      } else if (connection.type === Provider.Snowflake) {
        columns['geom'] = dedent`TO_GEOGRAPHY(
                                '${JSON.stringify(feature.geometry)}'
                              )`
      } else {
        columns['geom'] = dedent`${connection.processOutputGeogColumn(`ST_GEOMFROMGEOJSON(
'${JSON.stringify(feature.geometry)}'
)`)}`
      }
    }

    return dedent`SELECT
      ${Object.keys(columns)
        .map((x) => `${columns[x]} AS ${x}`)
        .join(',\n      ')}`
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { DrawCustomGeographies }
